import { isSameMonth, parseISO } from 'date-fns'
import { isArray, isNil, mergeWith } from 'lodash'

export const setPersistedDirector = (
  clientsToSearch: ClientVersion[] | undefined,
  director: InputMaybe<ChOfficerInput>,
) => {
  return clientsToSearch?.find((client) => {
    //Exclude middle name from first name to correct the matching between Acre clients and company house clients
    let indexOfFirstSpace = director?.firstNames?.indexOf(' ')
    let extractDirectorFirstName =
      indexOfFirstSpace !== -1 ? director?.firstNames?.substring(0, indexOfFirstSpace) : director?.firstNames
    let lowerCaseExtractDirectorFirstName = extractDirectorFirstName?.toLowerCase()
    let lowerCaseFullDirectorFirstName = director?.firstNames?.toLowerCase()
    let lowerCaseFullClientFirstName = client.details.first_name?.toLowerCase()

    //Exclude middle name from last name to correct the matching between Acre clients and company house clients
    let indexOfLastSpace = director?.lastName?.lastIndexOf(' ')
    let extractDirectorLastName =
      indexOfLastSpace !== -1 ? director?.lastName?.substring(-1, indexOfLastSpace) : director?.firstNames
    let lowerCaseExtractDirectorLastName = extractDirectorLastName?.toLowerCase()
    let lowerCaseFullDirectorLastName = director?.lastName?.toLowerCase()
    let lowerCaseFullClientLastName = client.details.last_name?.toLowerCase()

    return (
      //Get a match either when the full first/last name matches, or the extracted first/last matches
      (lowerCaseFullClientFirstName === lowerCaseExtractDirectorFirstName ||
        lowerCaseFullClientFirstName === lowerCaseFullDirectorFirstName) &&
      (lowerCaseFullClientLastName === lowerCaseExtractDirectorLastName ||
        lowerCaseFullClientLastName === lowerCaseFullDirectorLastName) &&
      client.details.date_of_birth &&
      director?.dateOfBirth &&
      // checking with isSameMonth due to companies house sending just month and year
      isSameMonth(parseISO(client.details.date_of_birth), parseISO(director?.dateOfBirth))
    )
  })
}

export const normaliseCompanyName = (name: Maybe<string>) => {
  return name?.toLowerCase().replace(/limited/gi, 'ltd')
}

export const setPersistedCompany = (companiesToSearch: ClientVersion[] | undefined, company: ChCompanyInput) => {
  return companiesToSearch?.find(
    (client) => normaliseCompanyName(client.details.organisation_name) === normaliseCompanyName(company.name),
  )
}

/**
 * Merges company data from Companies House into the target client object
 * only if the target fields are currently null or empty
 */
export const conditionalCompanyMerge = (target: Client, source: ClientInput): any => {
  const updatedCompany = { ...target }

  const isNullOrEmptyArray = (value: any) => {
    return isNil(value) || (Array.isArray(value) && value.length === 0)
  }

  const fieldsToUpdate: (keyof Pick<
    Client,
    'organisation_name' | 'organisation_sic' | 'limited_company_registration_number' | 'non_natural_organisation_type'
  >)[] = [
    'organisation_name',
    'organisation_sic',
    'limited_company_registration_number',
    'non_natural_organisation_type',
  ]

  // Update each field only if currently null and new data exists
  fieldsToUpdate.forEach((field) => {
    if (isNullOrEmptyArray(updatedCompany[field]) && source[field]) {
      updatedCompany[field] = source[field] as any
    }
  })

  // Update addresses only if currently null or empty array
  if (isNullOrEmptyArray(updatedCompany.addresses) && Array.isArray(source.addresses) && source.addresses.length > 0) {
    updatedCompany.addresses = source.addresses
  }

  return updatedCompany
}

// This function is used to update current client data with data from Companies House
// It overwrites fields in the current client data only if they are empty
// It also appends additional objects from arrays in the Companies House data to the current client data
export const conditionalMerge = (target: Client, source: ClientInput): any => {
  return mergeWith(target, source, (targetValue, sourceValue) => {
    if (isArray(sourceValue)) {
      if (isArray(targetValue)) {
        if (
          sourceValue.every((item) => typeof item === 'string') &&
          targetValue.every((item) => typeof item === 'string')
        ) {
          // Merge arrays of strings by combining unique values
          return Array.from(new Set([...targetValue, ...sourceValue]))
        } else {
          // Merge arrays of objects
          return targetValue
            .map((item, index) =>
              index >= sourceValue.length || !item || typeof item !== 'object'
                ? item
                : conditionalMerge(item, sourceValue[index] as ClientInput),
            )
            .concat(sourceValue.slice(targetValue.length))
        }
      }
      // Replace if target is not an array
      return sourceValue
    } else if (typeof sourceValue === 'object' && sourceValue !== null) {
      // Let Lodash handle merging objects
      return undefined
    } else if (isNil(targetValue)) {
      // Replace primitive values if target is null or undefined
      return sourceValue
    }
    // Default behavior for other cases
    return undefined
  })
}
